import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, MapPin, X, Loader2, Folder } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AddressGeocoderSearch — top-bar combined search.
 *  - Backend proxy (`/api/geocode/suggest`) so we get a proper User-Agent
 *    when hitting Nominatim → no more silent CORS / rate-limit fails that
 *    previously made the dropdown stay empty.
 *  - Merges in the user's project hits (name/address match) at the top,
 *    so typing a known project name jumps straight to it.
 *  - On address pick → calls `onResult({lat, lon, display_name, bbox})`.
 *  - On project pick → navigates to /projects/:id (or `onResult` if parent
 *    wants to handle it).
 */
const AddressGeocoderSearch = ({
  onResult,
  onProjectPick,
  placeholder = 'Rechercher une adresse, ville, projet…',
  testid = 'hub-address-search',
}) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    if (!q.trim() || q.trim().length < 3) {
      setItems([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const r = await axios.get(`${API}/geocode/suggest`, {
          params: { q, limit: 6 },
          withCredentials: true,
          signal: controller.signal,
          timeout: 12000,
        });
        setItems(r.data?.items || []);
      } catch (e) {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        setItems([]);
        setError(e?.response?.data?.detail || 'Recherche indisponible');
      } finally {
        setLoading(false);
      }
    }, 320);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handlePick = (it) => {
    setQ(it.label || it.sub || '');
    setOpen(false);
    if (it.kind === 'project') {
      if (onProjectPick) onProjectPick(it);
      else if (it.project_id) navigate(`/projects/${it.project_id}`);
      // Also pan the map if we have coords
      if (it.lat != null && it.lon != null) {
        onResult?.({ lat: it.lat, lon: it.lon, display_name: it.sub || it.label });
      }
    } else {
      onResult?.({
        lat: it.lat, lon: it.lon,
        display_name: it.sub || it.label,
        bbox: it.bbox,
      });
    }
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-[460px]" data-testid={testid}>
      <div
        className="flex items-center gap-2 rounded-lg px-2.5 h-9 transition-colors"
        style={{
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--c-gold)' }} />
        ) : (
          <Search className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,0.55)' }} />
        )}
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-white/35 text-white"
          data-testid={`${testid}-input`}
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(''); setItems([]); setError(null); }}
            className="text-white/40 hover:text-white"
            aria-label="Effacer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && q.trim().length >= 3 && (
        <div
          className="absolute left-0 right-0 mt-1 rounded-lg overflow-hidden z-[800]"
          style={{
            background: 'rgba(10,10,13,0.96)',
            border: '1px solid rgba(255,255,255,0.14)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 16px 40px rgba(0,0,0,0.55)',
          }}
          data-testid={`${testid}-dropdown`}
        >
          {error && (
            <div className="px-3 py-3 text-[12px] text-red-300/90" data-testid={`${testid}-error`}>
              {error}
            </div>
          )}
          {!error && items.length === 0 && !loading && (
            <div className="px-3 py-3 text-[12px] text-white/45">
              Aucun résultat — affine ta recherche.
            </div>
          )}
          {items.map((it, i) => (
            <button
              key={`${it.kind}-${it.project_id || it.label}-${i}`}
              type="button"
              onClick={() => handlePick(it)}
              className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-white/[0.04] transition-colors"
              data-testid={`${testid}-result-${i}`}
            >
              {it.kind === 'project' ? (
                <Folder className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: 'var(--c-gold)' }} />
              ) : (
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: 'var(--c-gold)' }} />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-[12.5px] text-white truncate">{it.label}</p>
                  {it.kind === 'project' && (
                    <span
                      className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={{ background: 'rgba(217,160,91,0.18)', color: 'var(--c-gold)' }}
                    >
                      Projet
                    </span>
                  )}
                </div>
                {it.sub && (
                  <p className="text-[10.5px] text-white/45 truncate">{it.sub}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressGeocoderSearch;
